import { HttpClient, HttpHeaders } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { DatatableModel } from '@ecap3/app-platform-datatable';
import { EcapGql } from '@ecap3/app-platform-graphql';
import {
    AggregateService,
    GqlQueryBuilderService,
    PagerService,
    PlatformDataService,
    SqlQueryBuilderService,
    UserInfoService,
    UtilityService
} from '@ecap3/core';
import { EcapBroadcastService } from '@ecap3/template-atlas';
import { forkJoin, Observable, of, Subscription } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { DELTA_CONFIG } from '../../delta-config/constant/delta-config.constant';
import { FileService } from './file.service';

@Injectable({
    providedIn: 'root'
})
export class CommonService {
    private navigationChangeData: string;
    public navigationChange: EventEmitter<any> = new EventEmitter<any>();
    private currentProjectId: string;
    private projectId: string;
    private subscription: Subscription[] = [];
    private profileImageSubscription: Subscription;

    private readonly header = new HttpHeaders({ 'Content-Type': 'application/json' });

    constructor(
        private snackBar: MatSnackBar,
        private sqlQueryBuilderService: SqlQueryBuilderService,
        private platformDataService: PlatformDataService,
        private utilityService: UtilityService,
        private aggregateService: AggregateService,
        private router: Router,
        private pagerService: PagerService,
        private GQL: EcapGql,
        private gqlQueryBuilderService: GqlQueryBuilderService,
        private fileService: FileService,
        private ecapBroadcastService: EcapBroadcastService,
        private readonly userInfoService: UserInfoService,
        private readonly http: HttpClient
    ) {}

    setNavigationChangeData(data) {
        this.navigationChangeData = data;
        // this.navigationChange.emit(this.navigationChangeData);
    }
    // navigation related works
    getNavigationChange(): Observable<any> {
        if (this.navigationChangeData) {
            return of(this.navigationChangeData);
        } else {
            return of(null);
        }
    }
    /* Generate new Guid from Core package */
    getNewGuid() {
        return this.utilityService.getNewGuid();
    }

    /* Generate new ISO date from Core package */
    getISODate(data) {
        return this.utilityService.getISODate(data);
    }

    parseGQLDataTableResponseData(
        response: any,
        dataTableModel: DatatableModel<any>
    ) {
        const results = response.Data;
        const totalRecords = response.TotalCount;
        const dataStore = { data: [], pager: {} };
        dataStore.data = results;
        dataStore.pager = this.pagerService.getPager(
            totalRecords,
            dataTableModel.CurrentPageNumber,
            dataTableModel.PageSize
        );
        return dataStore;
    }

    private createFilterFromConnectionObject(conneciton) {
        const filters = [];
        if (conneciton.ParentEntityName) {
            filters.push({
                property: 'ParentEntityName',
                operator: '=',
                value: conneciton.ParentEntityName
            });
        }
        if (conneciton.ParentEntityID) {
            filters.push({
                property: 'ParentEntityID',
                operator: '=',
                value: conneciton.ParentEntityID
            });
        }
        if (conneciton.ChildEntityName) {
            filters.push({
                property: 'ChildEntityName',
                operator: '=',
                value: conneciton.ChildEntityName
            });
        }
        if (conneciton.ChildEntityID) {
            filters.push({
                property: 'ChildEntityID',
                operator: '=',
                value: conneciton.ChildEntityID
            });
        }
        if (conneciton.Tags && conneciton.Tags.length > 0) {
            filters.push({
                property: 'Tags',
                operator: 'contains',
                value: [conneciton.Tags]
            });
        }
        return filters;
    }

    getAllConnectionByPropertyFilter(conneciton, parentExpend, childExpend) {
        const filters = [];
        if (conneciton.ParentEntityName) {
            filters.push({
                PropertyName: 'ParentEntityName',
                Value: conneciton.ParentEntityName
            });
        }
        if (conneciton.ParentEntityID) {
            filters.push({
                PropertyName: 'ParentEntityID',
                Value: conneciton.ParentEntityID
            });
        }
        if (conneciton.ChildEntityName) {
            filters.push({
                PropertyName: 'ChildEntityName',
                Value: conneciton.ChildEntityName
            });
        }
        if (conneciton.ChildEntityID) {
            filters.push({
                PropertyName: 'ChildEntityID',
                Value: conneciton.ChildEntityID
            });
        }
        if (conneciton.Tags) {
            filters.push({
                PropertyName: 'Tags',
                Value: conneciton.Tags
            });
        }
        const payload = {
            EntityName: 'Connection',
            IncludeConnection: true,
            DataFilters: filters
        };
        if (parentExpend) {
            payload['ExpandParent'] = parentExpend;
        }
        if (childExpend) {
            payload['ExpandChild'] = childExpend;
        }
        return this.platformDataService.getConnections(payload);
    }

    getConnectionIdsForChildOfParent(conneciton: any) {
        const filters = this.createFilterFromConnectionObject(conneciton);
        const fields = [DELTA_CONFIG.AllFieldsOfEntity.Connection];

        const query = this.sqlQueryBuilderService.prepareQuery(
            DELTA_CONFIG.Entity.Connection,
            fields,
            filters,
            null,
            0,
            100
        );

        return this.platformDataService
            .getBySqlFilter(DELTA_CONFIG.Entity.Connection, query)
            .pipe(
                map((response: any) => {
                    return response && response.body && response.body.Results;
                })
            );
    }

    disConnectChildParentRelationship(connectionModel) {
        return this.aggregateService.aggregateDisConnectModel(connectionModel);
    }

    insertAggregatePayload(entity: any, model: any) {
        return this.aggregateService.aggregateInsertModel(
            this.sqlQueryBuilderService.prepareInsertModel(entity, model)
        );
    }

    aggregateInsertModel(entity, model) {
        return this.aggregateService.aggregateInsertModel(
            this.sqlQueryBuilderService.prepareInsertModel(entity, model)
        );
    }

    aggregateConnectModel(connection) {
        return this.aggregateService.aggregateConnectModel(connection);
    }

    updateAggregatePayload(entity: any, model: any) {
        return this.aggregateService.aggregateUpdateModel(
            this.sqlQueryBuilderService.prepareInsertModel(entity, model)
        );
    }

    deleteAggregatePayload(entity: any, model: any) {
        return this.aggregateService.aggregateDeleteModel(
            this.sqlQueryBuilderService.prepareDeleteModel(entity, model)
        );
    }

    fetchData(entityName, fields?, filters?, orderBy?, pageNumber?, pageSize?) {
        const query = this.sqlQueryBuilderService.prepareQuery(
            entityName,
            fields,
            filters,
            null,
            0,
            100
        );
        return this.platformDataService.getBySqlFilter(entityName, query).pipe(
            map((response: any) => {
                return response.body.Results;
            })
        );
    }

    fetchResponseData(
        entityName,
        fields?,
        filters?,
        orderBy?,
        pageNumber?,
        pageSize?
    ) {
        const query = this.sqlQueryBuilderService.prepareQuery(
            entityName,
            fields,
            filters,
            null,
            0,
            100
        );
        return this.platformDataService.getBySqlFilter(entityName, query).pipe(
            map((response: any) => {
                return response;
            })
        );
    }

    fetchDataSingle(entityName, fields?, filters?, orderBy?) {
        const query = this.sqlQueryBuilderService.prepareQuery(
            entityName,
            fields,
            filters,
            orderBy,
            0,
            1
        );
        return this.platformDataService.getBySqlFilter(entityName, query).pipe(
            map((response: any) => {
                return response.body.Results ? response.body.Results[0] : null;
            })
        );
    }

    getConnectionsData(connectionData: any) {
        /*const query = this.sqlQueryBuilderService.prepareGetConnectionQuery(
            data['parentEntityName'] ? data['parentEntityName'] : null,
            data['parentEntityValue'] ? data['parentEntityValue'] : null,
            data['childEntityName'] ? data['childEntityName'] : null,
            data['childEntityValue'] ? data['childEntityValue'] : null,
            data['includeConnection'] ? data['includeConnection'] : false,
            data['tags'] ? data['tags'] : [],
            data['expandParent'] ? data['expandParent'] : false,
            data['expandChild'] ? data['expandChild'] : false,
            data['pageNumber'] ? data['pageNumber'] : 1,
            data['pageLimit'] ? data['pageLimit'] : 1,
        );


        return this.platformDataService.getConnections(query).pipe(map((response: any) => {
            const results = response['body'] ? response['body']['Results'] : null;
            return results;
        });*/

        return this.getConnectionIdsForChildOfParent(connectionData).pipe(
            switchMap((response: any) => {
                if (response && response.length > 0) {
                    return response;
                }

                return null;
            })
        );
    }

    getEntityImageConnectionId(conneciton) {
        const filters = [];

        filters.push({
            property: 'ParentEntityName',
            operator: '=',
            value: conneciton['ParentEntityName']
        });

        filters.push({
            property: 'ParentEntityID',
            operator: '=',
            value: conneciton['ParentEntityID']
        });

        filters.push({
            property: 'ChildEntityName',
            operator: '=',
            value: conneciton['ChildEntityName']
        });

        filters.push({
            property: 'Tags',
            operator: 'contains',
            // value: DELTA_CONFIG.Tags.FileOfClient,
            value: 'File-Of-Client-480-270'
        });
        filters.push({
            property: 'Tags',
            operator: 'contains',
            // value: DELTA_CONFIG.Tags.FileOfClient,
            value: 'File-Of-Client-200-200'
        });

        /* _.each(DELTA_CONFIG.ImageConversionSizes, (imageSize) => {
             filters.push({
                 property: 'Tags',
                 operator: '=',
                 value: [DELTA_CONFIG.Tags.FileOfClient + '-' + imageSize['Width'] + '-' + imageSize['Height'], 'Debug']
             });
         });*/

        const fields = [DELTA_CONFIG.AllFieldsOfEntity.Connection];

        const query = this.sqlQueryBuilderService.prepareQuery(
            DELTA_CONFIG.Entity.Connection,
            fields,
            filters,
            null,
            0,
            100
        );

        return this.platformDataService
            .getBySqlFilter(DELTA_CONFIG.Entity.Connection, query)
            .pipe(
                map((response: any) => {
                    return response.body ? response.body.Results : null;
                })
            );
    }

    removeConnectionAggregate(connectionModel) {
        return this.aggregateService.aggregateDisConnectModel(connectionModel);
    }

    executeAggregateCall(callList): Observable<any> {
        if (!callList?.length) return of(true);
        const calls$ = callList.map(item =>
            this.http.post(item.Uri, JSON.parse(item.Payload), { headers: this.header, withCredentials: true, observe: 'response' })
                .pipe(catchError(() => of({ body: null })))
        );
        return forkJoin(calls$).pipe(
            map(() => true),
            catchError((errorResponse) => {
                console.error('AggregateCallError: ', errorResponse);
                return of(false);
            })
        );
    }

    getProjectListForNavigation() {
        const filters = [];
        const fields = [
            'ItemId',
            'CreateDate',
            'CreatedBy',
            'Tags',
            'ProjectName',
            'ClientName',
            'StartDate',
            'EndDate',
            'IsCompleted',
            'IsDeleted',
            'ProjectImageId',
            'ProjectManager',
            'ProjectManagerId',
            'IsArchived',
            'Address',
            'TotalSite',
            'Latitude',
            'Longitude',
            'AdministrativeClientId',
            'RolesAllowedToRead'
        ];
        const Entity = 'Project';
        const query = this.sqlQueryBuilderService.prepareQuery(
            Entity,
            fields,
            filters,
            'CreateDate __desc',
            0,
            1
        );
        return this.platformDataService
            .getBySqlFilter(Entity, query, undefined)
            .pipe(
                map((response: Response) => {
                    const results = response.body['Results'];
                    return results;
                })
            );
    }

    getBySqlFilterAll(
        entity,
        fields,
        filters = [],
        orderBy = 'CreateDate __asc',
        pageNumber = 0,
        pageSize = 10
    ): Observable<any> {
        let results = [];
        const query = this.sqlQueryBuilderService.prepareQuery(
            entity,
            fields,
            filters,
            orderBy,
            pageNumber,
            pageSize
        );
        return this.platformDataService
            .getBySqlFilter(entity, query, undefined)
            .pipe(
                switchMap((response: Response) => {
                    const totalRecordCount = response.body['TotalRecordCount'];
                    results = results.concat(response.body['Results']);

                    // Get result data
                    if (totalRecordCount > pageSize) {
                        const noOfCall = Math.ceil(totalRecordCount / pageSize);
                        const multiCall$ = [];
                        for (let i = 0; i < noOfCall; i++) {
                            pageNumber = pageNumber + 1;
                            const queryMulty =
                                this.sqlQueryBuilderService.prepareQuery(
                                    entity,
                                    fields,
                                    filters,
                                    orderBy,
                                    pageNumber,
                                    pageSize
                                );
                            multiCall$.push(
                                this.platformDataService.getBySqlFilter(
                                    entity,
                                    queryMulty,
                                    undefined
                                )
                            );
                        }

                        return forkJoin(multiCall$ as Observable<any>[]).pipe(
                            map((resultsCallList) => {
                                if (
                                    resultsCallList &&
                                    resultsCallList.length > 0
                                ) {
                                    resultsCallList.forEach((_callList) => {
                                        results = results.concat(
                                            _callList['body']['Results']
                                        );
                                    });
                                }
                                return results;
                            })
                        );
                    }

                    return of(results);
                })
            );
    }

    /**
     * @desc Force empty field value when no autocomplete value is selected.
     * @param form
     * @param field
     */
    forceAutoCompleteSelection(form, field) {
        const autocompleteValue = form.get(field).value;
        if (autocompleteValue && !autocompleteValue['ItemId']) {
            form.get(field).setValue('');
        }
    }

    // download CSv file
    // downloadFile(fileDataUrl, fileName) {
    //     const FileSaver = require('file-saver');
    //     FileSaver.saveAs(fileDataUrl, fileName);
    // }

    downloadFile(fileDataUrl, fileName) {
        // window.open(fileDataUrl);
        window.location.href = fileDataUrl;
    }

    // downloadFile(data: Response) {
    //     const blob = new Blob([data], { type: 'text/csv' });
    //     const url= window.URL.createObjectURL(blob);
    //     window.open(url);
    // }

    showMessage(data) {
        const message = data.message ? data.message : '';
        this.snackBar.open(message, null, {
            duration:
                data.type && data.type === 'error'
                    ? DELTA_CONFIG.snackBar.DurationError
                    : DELTA_CONFIG.snackBar.Duration,
            verticalPosition: data.verticalPosition
                ? data.verticalPosition
                : DELTA_CONFIG.snackBar.VerticalPosition,
            horizontalPosition: data.horizontalPosition
                ? data.horizontalPosition
                : DELTA_CONFIG.snackBar.HorizontalPosition,
            panelClass:
                data.type && data.type === 'error'
                    ? ['snackbar-error']
                    : DELTA_CONFIG.snackBar.PanelClass
        });
    }

    getPraxisUserInfos(
        dataTableModel: DatatableModel<any>,
        searchKey?: string,
        filterList?: any[]
    ): Observable<any> {
        const filters = [];

        if (filterList && filterList.length) {
            for (let i = 0, ilen = filterList.length; i < ilen; i++) {
                filters.push(filterList[i]);
            }
        }

        if (searchKey) {
            filters.push({
                property: 'DisplayName',
                operator: 'search',
                value: searchKey
            });
        }

        const entityName = DELTA_CONFIG.Entity.Person;

        const fields = DELTA_CONFIG.AllFieldsOfEntity.Person;

        const sortBy =
            dataTableModel.SortBy +
            ': ' +
            (dataTableModel.SortDir === 'desc' ? '-1' : '+1');

        return this.GQL.watchQuery<any>({
            query: this.gqlQueryBuilderService.prepareQuery(
                entityName,
                fields,
                filters,
                sortBy,
                dataTableModel.CurrentPageNumber,
                dataTableModel.PageSize
            )
        }).valueChanges.pipe(
            map((response: any) => {
                if (response.data && response.data.PraxisUsers) {
                    return this.parseGQLDataTableResponseData(
                        response.data.PraxisUsers,
                        dataTableModel
                    );
                }

                console.error(response);
                return null;
            })
        );
    }

    getPraxisUserInfoByUserId(userId: string): Observable<any> {
        const filters = [];
        filters.push({
            property: 'UserId',
            operator: '=',
            value: userId
        });
        const entityName = DELTA_CONFIG.Entity.Person;
        const fields = DELTA_CONFIG.AllFieldsOfEntity.Person;
        const sortBy = 'CreateDate: -1';
        return this.GQL.watchQuery<any>({
            query: this.gqlQueryBuilderService.prepareQuery(
                entityName,
                fields,
                filters,
                sortBy,
                0,
                1
            )
        }).valueChanges.pipe(
            map((response: any) => {
                if (response && response.data && response.data.PraxisUsers) {
                    return (
                        response.data.PraxisUsers.Data &&
                        response.data.PraxisUsers.Data[0]
                    );
                }
                console.error(response);
                return null;
            })
        );
    }

    loadProfileImage(userId?): void {
        userId = userId || this.userInfoService.userInfo.UserId;
        this.profileImageSubscription = this.getPraxisUserInfoByUserId(
            userId
        ).subscribe({
            next: (res) => {
                if (!res) {
                    return;
                }
                const praxisUser = res;
                const fileId = res.Image.FileId;
                this.fileService.getFileById(fileId).subscribe({
                    next: (_responce) => {
                        if (_responce) {
                            praxisUser['ProfileImage'] = _responce.Url;
                            this.ecapBroadcastService.broadcastUserInfoEvent(
                                praxisUser
                            );
                        }
                    }
                });
            }
        });
    }

    unsubscribeProfileImage() {
        if (this.profileImageSubscription) {
            this.profileImageSubscription.unsubscribe();
        }
    }

    getDateTimeDiffWithCurrentDate(
        //  the date which need to be calculated
        dateSent,
        //send true if you want the difference in days
        inDays?: boolean,
        //if  you want the difference in hours
        inHrs?: boolean,
        // if you want the difference in minutes
        inMinutes?: boolean
    ) {
        let currentDate = new Date();
        dateSent = new Date(dateSent);
        let dividingFactor = 1000 * 60 * 60 * 24;
        let val = 0;
        if (inDays) {
            dividingFactor = 1000 * 60 * 60 * 24;
            val =
                (Date.UTC(
                    currentDate.getFullYear(),
                    currentDate.getMonth(),
                    currentDate.getDate()
                ) -
                    Date.UTC(
                        dateSent.getFullYear(),
                        dateSent.getMonth(),
                        dateSent.getDate()
                    )) /
                dividingFactor;
        } else if (inHrs) {
            dividingFactor = 1000 * 60 * 60;
            val =
                (Date.UTC(
                    currentDate.getFullYear(),
                    currentDate.getMonth(),
                    currentDate.getDate(),
                    currentDate.getHours()
                ) -
                    Date.UTC(
                        dateSent.getFullYear(),
                        dateSent.getMonth(),
                        dateSent.getDate(),
                        dateSent.getHours()
                    )) /
                dividingFactor;
        } else if (inMinutes) {
            dividingFactor = 1000 * 60;
            val =
                (Date.UTC(
                    currentDate.getFullYear(),
                    currentDate.getMonth(),
                    currentDate.getDate(),
                    currentDate.getHours(),
                    currentDate.getMinutes()
                ) -
                    Date.UTC(
                        dateSent.getFullYear(),
                        dateSent.getMonth(),
                        dateSent.getDate(),
                        dateSent.getHours(),
                        dateSent.getMinutes()
                    )) /
                dividingFactor;
        }

        return val;
    }
}
